import { useState, useEffect } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useLayout } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { overlayBackdrop } from "../../theme/styles";
import { dbGetLoginLog } from "../../db/loginLog";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

function relativeTime(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr${Math.floor(diff / 3600) !== 1 ? "s" : ""} ago`;
  return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) !== 1 ? "s" : ""} ago`;
}

function formatDateTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

export function LoginLogModal({ onClose }) {
  const { SA, GLASS, isDark, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const { isWide } = useLayout();
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dbGetLoginLog().then(rows => { setItems(rows); setLoading(false); });
  }, []);

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200, display: "flex", alignItems: isWide ? "center" : "flex-end", justifyContent: isWide ? "center" : "stretch", padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal, borderRadius: isWide ? 16 : "16px 16px 0 0", width: isWide ? 560 : "100%", maxHeight: isWide ? "80vh" : "88vh", display: "flex", flexDirection: "column", boxSizing: "border-box", animation: isWide ? "modalSlideUp 0.25s cubic-bezier(0.16,1,0.3,1)" : "sheetSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)", ...dragStyle }}>
        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}` }}>
          <span style={{ fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.2em", textTransform: "uppercase" }}>Login Log</span>
          <Button variant="icon" onClick={onClose} style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "12px 20px 30px" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.label, letterSpacing: "0.2em", padding: "40px 0", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>LOADING...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.label, letterSpacing: "0.2em", padding: "40px 0", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>NO LOGINS IN PAST 7 DAYS</div>
          ) : items.map(item => (
            <div key={item.id} style={{ background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: RADIUS.md, padding: "12px 16px", marginBottom: 8, border: `1px solid ${SA.borderGray}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", color: SA.accent, background: "rgba(91,142,255,0.15)", padding: "2px 8px", borderRadius: 2, border: "1px solid rgba(91,142,255,0.3)" }}>
                    {item.airline}
                  </span>
                  {item.display_name && (
                    <span style={{ fontSize: TYPOGRAPHY.body, color: SA.white, fontWeight: 500 }}>{item.display_name}</span>
                  )}
                </div>
                <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", flexShrink: 0, marginLeft: 8 }}>{relativeTime(item.logged_in_at)}</span>
              </div>
              <div style={{ fontSize: TYPOGRAPHY.note, color: SA.lightSilver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.04em" }}>{item.email}</div>
              <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 3, letterSpacing: "0.02em" }}>{formatDateTime(item.logged_in_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
